import type { IntlShape } from 'react-intl';
import { FreeTrialCoupon } from '@alltrails/core/types/Coupon';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import type SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import type Coupon from '@alltrails/types/Coupon';
import type { Plan } from '@alltrails/types/Plan';
import { getPriceTotal } from './plan';
import toMinorUnits from './toMinorUnits';

type GetApplePayConfigForStripeParams = {
  canUpgradeExistingSubscription: boolean;
  coupon?: Coupon;
  isMultiyearConversion?: boolean;
  plan: Plan;
  publicBaseUrl: string;
  purchaseTier: PurchaseTier;
  subscription?: SubscriptionsNewResponse['subscription'];
  whenExpired?: string;
};

const getIntervalUnit = (unit: FreeTrialCoupon['freeTrialUnit']) => {
  switch (unit) {
    case 'day':
      return 'day' as const;
    case 'week':
      return 'day' as const; // Apple Pay doesn't support 'week', use 'day' instead
    case 'month':
      return 'month' as const;
  }
};

function getFreeTrialCouponDate(coupon: FreeTrialCoupon) {
  const date = new Date();

  switch (coupon.freeTrialUnit) {
    case 'day':
      date.setDate(date.getDate() + coupon.freeTrialAmount);
      break;
    case 'week':
      date.setDate(date.getDate() + coupon.freeTrialAmount * 7);
      break;
    case 'month':
      date.setMonth(date.getMonth() + coupon.freeTrialAmount);
      break;
  }

  return date;
}

/**
 * Builds the payment request config that Stripe expects for Apple Pay (and similar
 * payment sheets). This is Stripe's interpretation of Apple Pay—the shape is
 * Stripe's paymentRequest / recurringPaymentRequest API, not raw Apple Pay config.
 */
export default function getApplePayConfigForStripe(
  {
    canUpgradeExistingSubscription,
    coupon,
    isMultiyearConversion,
    plan,
    publicBaseUrl,
    purchaseTier,
    subscription,
    whenExpired
  }: GetApplePayConfigForStripeParams,
  intl: IntlShape
) {
  const managementURL = `${publicBaseUrl}/my/profile/manage-subscription`;

  const regularBillingLabel =
    purchaseTier === 'peak'
      ? intl.formatMessage({ defaultMessage: 'Peak subscription' })
      : intl.formatMessage({ defaultMessage: 'Plus subscription' });

  const trialBillingLabel = intl.formatMessage({ defaultMessage: 'Trial' });

  // Multiyear conversion: recurringPaymentStartDate is when_expired from subscriptions/new.
  if (isMultiyearConversion && whenExpired) {
    return {
      managementURL,
      paymentDescription: '', // does not appear to be used in the payment sheet apple pay documentation might be wrong?
      regularBilling: {
        label: regularBillingLabel,
        amount: toMinorUnits(getPriceTotal(plan, coupon), plan.currency),
        recurringPaymentIntervalUnit: 'year' as const,
        recurringPaymentStartDate: new Date(whenExpired)
      }
    };
  }

  // Free trial coupon: trial end comes from coupon.freeTrialAmount / freeTrialUnit (see getFreeTrialCouponDate).
  if (coupon?.type === 'FreeTrialCoupon') {
    const date = getFreeTrialCouponDate(coupon);

    return {
      managementURL,
      paymentDescription: '', // does not appear to be used in the payment sheet apple pay documentation might be wrong?
      regularBilling: {
        label: regularBillingLabel,
        amount: toMinorUnits(getPriceTotal(plan, coupon), plan.currency),
        recurringPaymentIntervalUnit: 'year' as const,
        recurringPaymentStartDate: date
      },
      trialBilling: {
        amount: 0,
        label: trialBillingLabel,
        recurringPaymentEndDate: date,
        recurringPaymentIntervalUnit: getIntervalUnit(coupon.freeTrialUnit)
      }
    };
  }

  // Plus-to-Peak upgrade while still in trial: bill at current period end (subscription.currentPeriodEndDate).
  if (canUpgradeExistingSubscription && subscription?.trial) {
    const endOfTrial = new Date(subscription.currentPeriodEndDate);

    return {
      managementURL,
      paymentDescription: '', // does not appear to be used in the payment sheet apple pay documentation might be wrong?
      regularBilling: {
        amount: toMinorUnits(getPriceTotal(plan, coupon), plan.currency),
        label: regularBillingLabel,
        recurringPaymentIntervalUnit: 'year' as const,
        recurringPaymentStartDate: endOfTrial
      },
      trialBilling: {
        amount: 0,
        label: trialBillingLabel,
        recurringPaymentEndDate: endOfTrial,
        recurringPaymentIntervalUnit: 'day' as const
      }
    };
  }

  // Normal trial plan: trial ends 7 days from now (compute at request time).
  if (plan.isTrial) {
    const endOfTrial = new Date();
    endOfTrial.setDate(endOfTrial.getDate() + 7);

    return {
      managementURL,
      paymentDescription: '', // does not appear to be used in the payment sheet apple pay documentation might be wrong?
      regularBilling: {
        amount: toMinorUnits(getPriceTotal(plan, coupon), plan.currency),
        label: regularBillingLabel,
        recurringPaymentIntervalUnit: 'year' as const,
        recurringPaymentStartDate: endOfTrial
      },
      trialBilling: {
        amount: 0,
        label: trialBillingLabel,
        recurringPaymentEndDate: endOfTrial,
        recurringPaymentIntervalUnit: 'day' as const
      }
    };
  }

  // Paid subscription (no trial line item). Omit recurringPaymentStartDate since Stripe validates it and it already defaults to now.
  return {
    managementURL,
    paymentDescription: '', // does not appear to be used in the payment sheet apple pay documentation might be wrong?
    regularBilling: {
      amount: toMinorUnits(getPriceTotal(plan, coupon), plan.currency),
      label: regularBillingLabel,
      recurringPaymentIntervalUnit: 'year' as const
    }
  };
}
